
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { AdhocMaintenanceItem } from '@/models/AdhocMaintenance.model';
import { JobCard, JobType, JobCardLineItem, JobCardVehicleOdometers } from '@/models/JobCard.model';
import { Vehicle } from '@/models/Vehicle.model';

// --- Services ---
import AppService from '@/services/app';
import AdhocMaintenanceService from '@/services/adhocMaintenance';
import JobCardService from '@/services/jobCard';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component
export default class JobCardListing extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  // used by Filter Area
  search: string = '';
  filterOptions: string[] = ['Open', 'Completed'];
  statusFilter: string = 'Open';

  // used by No Data Slot
  isLoading: boolean = true;

  displayJobCards: JobCard[] = [];
  expandDelay: boolean = false;

  // used by Create Item Dialog
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: JobCard = {
    id: 0,
    vehicle1Id: 0,
    vehicle1Code: '',
    vehicle1TypeId: 0,
    vehicle1Type: '',
    startDate: '',
    jobTypeId: 0,
    jobType: '',  
    originalEta: '',
    lineItemCount: 0,  
    createdById: 0,
    createdBy: '',
  };

  activeVehicles: Vehicle[] = [];
  selectedVehicle1: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  selectedVehicle2: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  disableLinkedTrailer: boolean = false;

  startDatePicker: boolean = false;
  etaDatePicker: boolean = false;
  etaTab: string = '';
  etaDate: string = '';
  etaTime: string = '';

  selectedJobType: JobType = {
    id: 0,
    type: '',
    requiresOdo: false,
  };

  adhocMaintenanceItems: AdhocMaintenanceItem[] = [];
  selectedAdhocMaintenance: AdhocMaintenanceItem[] = [];

  vehicleOdometers: JobCardVehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);

  valid: boolean = false;
  clicked: boolean = false;

  // used by Update ETA Dialog
  showUpdateEtaDialog: boolean = false;
  updateValid: boolean = false;
  updateClicked: boolean = false;
  updatedEta: string = '';

  // used by Add Line Item Dialog
  lineItems: JobCardLineItem[] = [];
  showAddLineItemDialog: boolean = false;
  addLineItemDialogTitle: string = '';
  newLineItems: JobCardLineItem[] = [];
  newLineItem: JobCardLineItem = Object.assign({}, this.$store.getters['jobCards/emptyLineItem']);
  lineItemFormValid: boolean = false;
  lineItemCounter: number = 0;
  addLineItemsClicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredObject: (value: any) => value.id > 0,
    requiredNumber: (value: number) => value != null && value !== 0 || '',
    requiredNumberCanBeZero: (value: number) => value != null || '',
    currency: (value: string) =>  /^\d{0,8}(?:\.\d{1,2})?$/.test(value) || 'Limited to two decimal places',
    maxLineItemCost: (value: number | string) => {
      if ((typeof value) === 'string') {
        return !!value && parseFloat('' + value) < 100000 || '';
      } else {
        return value < 100000 || '';
      }
    },
    numbersOnly: (value: string) => (/^\d+$/.test(value)) || 'Invalid character',
    maxOdometer: (value: number) => value < 2000000 || 'Invalid',
  };

  // used by Confirm Action Dialog for Close/Approve
  action: string = '';
  dialogContent: string = '';
  confirmActionDelay: boolean = false;
  showConfirmActionDialog: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      if (this.statusFilter === 'Completed') {
        return [
          {
            text: '#',
            value: 'id',
          },
          {
            text: 'Vehicle(s)',
            value: 'vehicleCodes',
          },
          {
            text: 'Start Date',
            value: 'startDate',
          },
          {
            text: 'Type',
            value: 'jobType',
          },
          {
            sortable: false,
            text: 'Description',
            value: 'description',
          },
          {
            text: 'Completion ETA',
            value: 'completionEta',
          },
          {
            sortable: false,
            text: 'Original ETA',
            value: 'originalEta',
          },
          {
            sortable: false,
            text: 'Reason',
            value: 'adjustedEtaReason',
          },
          {
            text: 'Completed On',
            value: 'closedAt',
          },
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      } else {
        return [
          {
            text: '#',
            value: 'id',
          },
          {
            text: 'Vehicle(s)',
            value: 'vehicleCodes',
          },
          {
            text: 'Start Date',
            value: 'startDate',
          },
          {
            text: 'Type',
            value: 'jobType',
          },
          {
            sortable: false,
            text: 'Description',
            value: 'description',
          },
          {
            text: 'Completion ETA',
            value: 'completionEta',
          },
          {
            sortable: false,
            text: 'Original ETA',
            value: 'originalEta',
          },
          {
            sortable: false,
            text: 'Reason',
            value: 'adjustedEtaReason',
          },
          {
            sortable: false,
            text: '',
            value: 'action',
            align: 'end',
          },
        ];
      }
    }

    customFilter(column: string, search: string, item: JobCard) {
      let result: boolean = false;

      if (search === '') {
        result = true;
      } else {
        result = item.vehicle1Code.includes(search.toLowerCase()) ||
          (item.vehicle2Code ? item.vehicle2Code.toLowerCase().includes(search.toLowerCase()) : false) ||
          item.startDate.includes(search) ||
          (item.secondaryJobType ? item.secondaryJobType.toLowerCase().includes(search.toLowerCase()) : item.jobType.toLowerCase().includes(search.toLowerCase())) ||
          (item.adjustedEta ? item.adjustedEta.includes(search) : item.originalEta.includes(search));

        if (this.statusFilter === 'Completed') {
          result = result || item.closedAt!.includes(search);
        }
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'vehicleCodes':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => a.vehicle1Code.toLowerCase().localeCompare(b.vehicle1Code.toLowerCase()));
          } else {
            items.sort((a: JobCard, b: JobCard) => b.vehicle1Code.toLowerCase().localeCompare(a.vehicle1Code.toLowerCase()));
          }
          break;

        case 'startDate':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => a.startDate.localeCompare(b.startDate));
          } else {
            items.sort((a: JobCard, b: JobCard) => b.startDate.localeCompare(a.startDate));
          }
          break;

        case 'jobType':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.secondaryJobType ? a.secondaryJobType : a.jobType).toLowerCase().localeCompare((b.secondaryJobType ? b.secondaryJobType : b.jobType).toLowerCase()));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.secondaryJobType ? b.secondaryJobType : b.jobType).toLowerCase().localeCompare((a.secondaryJobType ? a.secondaryJobType : a.jobType).toLowerCase()));
          }
          break;

        case 'completionEta':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => (a.adjustedEta ? a.adjustedEta : a.originalEta).localeCompare((b.adjustedEta ? b.adjustedEta : b.originalEta)));
          } else {
            items.sort((a: JobCard, b: JobCard) => (b.adjustedEta ? b.adjustedEta : b.originalEta).localeCompare((a.adjustedEta ? a.adjustedEta : a.originalEta)));
          }
          break;
        
        case 'closedAt':
          if (sortDesc[0]) {
            items.sort((a: JobCard, b: JobCard) => a.closedAt!.localeCompare(b.closedAt!));
          } else {
            items.sort((a: JobCard, b: JobCard) => b.closedAt!.localeCompare(a.closedAt!));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await VehicleService.listTypes(false);
      await VehicleService.listActive(false);
      await JobCardService.listTypes(false);
      await AdhocMaintenanceService.listAll(true);
      await this.listOpen(true);

      this.filterJobCards();

      // !!TODO: We don't know which vehicle will be selected when they create a new job
      // and we don't want to fetch Adhoc Maintenance every time they change the vehicles in the create dialog
      // at some point we prob need to put in an auto-fetcher like we have on scrubbill, but not sure how often it would really need to run

      // if we have vehicle id params, we came from Daily Yard List and need to do something now
      const vehicle1Id: string = this.hashids.decode(this.$route.params.vehicle1) + '';
      if (vehicle1Id && vehicle1Id.length > 0) {
        this.launchNewItemDialog(vehicle1Id, this.hashids.decode(this.$route.params.vehicle2) + '');
      }
    }

    async listOpen(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await JobCardService.listOpen(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async listClosed(refreshFromAPI: boolean) {
      try {
        this.isLoading = true;
        const response: string = await JobCardService.listClosed(refreshFromAPI);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    async listLineItems(jobCard: JobCard) {
      try {
        this.lineItems = [];
        const response = await JobCardService.listLineItemsByJobCard({ params: { jobCardId: jobCard.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            if (response.data.message !== 'No Data Found') {
              AppService.errorHandler(response.data.message);
            }
            this.lineItems = [];
          } else {
            this.lineItems = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.lineItems = [];
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - listLineItems - 1731 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async getJobCardVehicleOdometers(jobCard: JobCard) {
      try {
        this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
        const response = await JobCardService.getJobCardVehicleOdometers({ params: { jobCardId: jobCard.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            if (response.data.message !== 'No Data Found') {
              AppService.errorHandler(response.data.message);
            }
            this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
          } else {
            this.vehicleOdometers = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - getJobCardVehicleOdometers - 1731 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async filterJobCards() {
      switch (this.statusFilter) {
        case ('Open'):
          await this.listOpen(false);
          this.displayJobCards = this.$store.getters['jobCards/openJobCards'];
          break;

        case ('Completed'):
          await this.listClosed(false);
          this.displayJobCards = this.$store.getters['jobCards/closedJobCards'];
          break;
      }
    }

    jobTypeRequiresOdo(item: JobCard) {
      return this.$store.getters['jobCards/jobTypes'].find((type: JobType) => type.id === item.jobTypeId).requiresOdo;
    }

    async expandJobCard(item: JobCard, expand: any) {
      this.lineItems = [];
      this.vehicleOdometers = Object.assign({}, this.$store.getters['jobCards/emptyVehicleOdometers']);

      if (item.jobType === 'Service' || item.jobType === 'Wheel Alignment') {
        // go fetch the vehicle ODO info for this load
        try {
          this.expandDelay = true;
          await this.getJobCardVehicleOdometers(item);
        } finally {
          this.expandDelay = false;
        }
      } else if (item.jobType === 'Maintenance' && item.lineItemCount && item.lineItemCount > 0) {
        // go fetch the lineItems for this load
        try {
          this.expandDelay = true;
          await this.listLineItems(item);
        } finally {
          this.expandDelay = false;
        }
      }

      expand(true);
    }

    closeRow(expand: any) {
      expand(false);
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowAddLineItemsFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowEditEtaFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowCloseFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 5},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowApproveFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Workshop User', level: 2},
      ]);
    }
  // #endregion

  // #region Functions required for the Create/Update Item Dialog
    getAdhocMaintenanceItems() {
      return this.$store.getters['adhocMaintenance/adhocMaintenance'].filter((item: AdhocMaintenanceItem) => {
        return !item.jobCardId && 
        (
          // we always have a vehicle1 on adhoc maintenance AND daily yard list
          (item.vehicle1Id === this.selectedVehicle1.id) ||
          // we might ONLY have a vehicle2 on adhoc maintenance
          (item.vehicle2Id && item.vehicle2Id === this.selectedVehicle1.id) ||
          // we might ONLY have a vehicle2 on daily yard list
          (this.selectedVehicle2 && item.vehicle1Id === this.selectedVehicle2.id) ||
          // we might have a vehicle2 on adhoc maintenance AND daily yard list
          (item.vehicle2Id && this.selectedVehicle2 && item.vehicle2Id === this.selectedVehicle2.id)
        );
      });
    }

    selectVehicle1(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle1 = selectedVehicle;
        this.selectVehicle2();
        this.disableLinkedTrailer = false;
      } else {
        // likely was cleared so is now undefined
        this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }

      this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      if (this.clickedItem.id < 1 ) {
        // don't reset description on an update
        this.clickedItem.description = '';
      }

      this.selectedAdhocMaintenance = [];
      // check for Adhoc Maintenance items
      this.adhocMaintenanceItems = this.getAdhocMaintenanceItems();
      if (this.clickedItem.id < 1 && this.adhocMaintenanceItems.length > 0) {
        // default selectedJobType to Adhoc Maintenance automatically, but only if this is NOT an update!! (can only update Service & Wheel Alignment Jobs)
        this.selectedJobType = this.$store.getters['jobCards/jobTypes'].find((item: JobType) => item.secondaryType === 'Adhoc Maintenance');
      } else {
        this.selectedJobType = this.selectedJobType || {
          id: 0,
          type: '',
          requiresOdo: false,
        };
      }
    }

    getAvailableTrailers() {
      // reset back to ALL trailers for Trailer2
      let availableRearTrailers = this.$store.getters['vehicles/vehiclesByType']('Trailer');

      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.isLinked || (item.isLinked && !item.isFront && item.id === this.selectedVehicle1.linkedTrailerId)  ); // hide the other linked trailers

      if (this.selectedVehicle1.isLinked) {
        this.disableLinkedTrailer = true;
        var index = availableRearTrailers.findIndex((item: Vehicle) => item.id === this.selectedVehicle1.linkedTrailerId);
        if (index >= 0) {
          this.selectVehicle2(availableRearTrailers[index]);
        }
      }

      return availableRearTrailers.filter((item: Vehicle) => item.id !== this.selectedVehicle1.id);
    }

    selectVehicle2(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle2 = selectedVehicle;

        // no need to check for ad hoc maintenance on selectedVehicle 
        // any longer as the link has been forced going forward
      } else {
        // was cleared
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }
    }

    getOneWeekPast() {
      return dayjs().add(-1, 'week').format('YYYY-MM-DD');
    }

    closeEtaPicker() {
      this.clickedItem.originalEta = this.etaDate + ' ' + this.etaTime;
      this.etaDatePicker = false;

      // prevents animate to date tab when closing
      setTimeout(() => (this.etaTab = "date"), 300);
    }

    getSecondaryJobTypeOptions() {
      let secondaryJobTypes: JobType[] = this.$store.getters['jobCards/jobTypes'].filter((item: JobType) => item.id === this.selectedJobType.id && item.secondaryId);
      if (this.selectedJobType.type === 'Maintenance') {
        secondaryJobTypes = secondaryJobTypes.reduce((a: any, b: any) => {
          let newItem = b;
          if (newItem.secondaryType === 'Adhoc Maintenance') {
            newItem.disabled = false;
            if (this.adhocMaintenanceItems.length < 1) {
              newItem.disabled = true;
            }
          }

          return a.concat(newItem);
        }, []);
      }
      return secondaryJobTypes;
    }

    maintenanceHeaders() {
      return [
        {
          text: 'Date Reported',
          value: 'dateReported',
        },
        {
          text: 'Vehicle',
          value: 'vehicleCodes',
        },        
        {
          text: 'Description',
          value: 'description',
        },
      ];
    }

    selectAdhocMaintenanceItem(selectedItem: any) {
      this.clickedItem.description = selectedItem.item.description;
    }

    maintenanceSelected() {
      // if selected job type is adhoc maintenance, they need to have selected at least 1!
      return this.selectedJobType.secondaryType !== 'Adhoc Maintenance' || (this.selectedJobType.secondaryType === 'Adhoc Maintenance' && this.selectedAdhocMaintenance.length > 0);
    }

    async launchNewItemDialog(vehicle1Id: string | undefined, vehicle2Id: string | undefined) {
      this.clickedItem = {
        id: 0,
        vehicle1Id: 0,
        vehicle1Code: '',
        vehicle1TypeId: 0,
        vehicle1Type: '',
        startDate: dayjs().format('YYYY-MM-DD'),
        jobTypeId: 0,
        jobType: '',  
        originalEta: '',
        description: '',
        lineItemCount: 0,  
        createdById: 0,
        createdBy: '',
      };

      // filter horse / trailer / front linked trailers ONLY
      this.activeVehicles = this.$store.getters['vehicles/activeVehicles'];
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.isLinked || (item.isLinked && item.isFront) ); // hide the rear links 

      this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);

      // if they came from Daily Yard List, preselect the relevant Vehicles
      if (vehicle1Id) {
        this.selectedVehicle1 = this.$store.getters['vehicles/vehicleById'](+vehicle1Id);

        if (vehicle2Id) {
          this.selectedVehicle2 = this.$store.getters['vehicles/vehicleById'](+vehicle2Id);
        }
      }

      this.etaDate = '';
      this.etaTime = '';

      this.selectedJobType = {
        id: 0,
        type: '',
        requiresOdo: false,
      }

      this.selectedAdhocMaintenance = [];

      this.valid = false;
      this.title = 'Create New Job Card'

      this.$nextTick(() => {
        if (this.$refs.itemForm) {
          (this.$refs.itemForm as any).resetValidation();
        }
      });

      this.showItemDialog = true;
    }

    async launchUpdateItemDialog(item: JobCard) {
      this.clickedItem = Object.assign({}, item);

      // filter horse / trailer / front linked trailers ONLY
      this.activeVehicles = this.$store.getters['vehicles/activeVehicles'];
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.isLinked || (item.isLinked && item.isFront) ); // hide the rear links 
      
      this.selectedVehicle1 = this.$store.getters['vehicles/vehicleById'](this.clickedItem.vehicle1Id);
      if (this.clickedItem.vehicle2Id) {
        this.selectedVehicle2 = this.$store.getters['vehicles/vehicleById'](this.clickedItem.vehicle2Id);
      }

      this.selectedJobType = this.$store.getters['jobCards/jobTypes'].find((item: JobType) => item.id === this.clickedItem.jobTypeId && item.secondaryId === this.clickedItem.secondaryJobTypeId);
      
      this.title = 'Update Job Card'

      this.$nextTick(() => {
        if (this.$refs.itemForm) {
          (this.$refs.itemForm as any).resetValidation();
        }
      });

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await JobCardService.create({
          vehicle1Id: this.selectedVehicle1.id,
          vehicle2Id: this.selectedVehicle2.id,
          startDate: this.clickedItem.startDate,
          originalEta: this.clickedItem.originalEta,
          jobTypeId: this.selectedJobType.id,
          secondaryJobTypeId: this.selectedJobType.secondaryId,
          odometer: this.clickedItem.odometer,
          adhocMaintenanceId: (this.selectedJobType.secondaryType === 'Adhoc Maintenance' && this.selectedAdhocMaintenance.length > 0) ? this.selectedAdhocMaintenance[0].id : undefined,
          description: this.clickedItem.description,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listOpen(true);
            this.filterJobCards();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doCreate - 2060 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await JobCardService.update({
          id: this.clickedItem.id,
          odometer: this.clickedItem.odometer,
          description: this.clickedItem.description,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listClosed(true);
            this.filterJobCards();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doUpdate - 2097 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Update ETA Dialog
    closeUpdateEtaPicker() {
      this.updatedEta = this.etaDate + ' ' + this.etaTime;
      this.etaDatePicker = false;

      // prevents animate to date tab when closing
      setTimeout(() => (this.etaTab = "date"), 300);
    }

    launchUpdateEtaDialog(item: JobCard) {
      this.clickedItem = Object.assign({}, item);

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.valid = false;

      this.updatedEta = this.clickedItem.adjustedEta ? this.clickedItem.adjustedEta : this.clickedItem.originalEta;
      if (dayjs().isAfter(dayjs(this.updatedEta))) {
        this.updatedEta = dayjs().format('YYYY-MM-DD HH:mm');
      } else {
        this.updatedEta = dayjs(this.updatedEta).add(1, 'day').format('YYYY-MM-DD HH:mm');
      }

      this.etaDate = dayjs(this.updatedEta).format('YYYY-MM-DD');
      this.etaTime = dayjs(this.updatedEta).format('HH:mm');

      this.$nextTick(() => {
        if (this.$refs.updateForm) {
          (this.$refs.updateForm as any).resetValidation();
        }
      });

      this.showUpdateEtaDialog = true;
    }

    async doUpdateEta() {
      try {
        this.updateClicked = true;

        const response = await JobCardService.updateEta({
          id: this.clickedItem.id,
          adjustedEta: this.updatedEta,
          adjustedEtaReason: (this.clickedItem.adjustedEtaReason ? this.clickedItem.adjustedEtaReason : ''),
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listOpen(true);
            this.filterJobCards();
            this.showUpdateEtaDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doUpdateEta - 2175 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.updateClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Line Items Dialog
    lineItemHeaders() {
      return [
        {
          text: 'Description',
          value: 'description',
        },
        {
          text: 'Cost',
          value: 'cost',
          align: 'end',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    async launchAddLineItemDialog(item: JobCard) {
      this.clickedItem = Object.assign({}, item);

      await this.listLineItems(item);

      this.addLineItemDialogTitle = 'Add Line Item(s) for Job Card #' + this.clickedItem.id;
      this.lineItemCounter = 0;
      this.newLineItems = [];
      this.newLineItem = Object.assign({}, this.$store.getters['jobCards/emptyLineItem']);

      this.$nextTick(() => {
        if (this.$refs.addLineItemForm) {
          ((this.$refs.addLineItemForm) as any).resetValidation();
        }
      });

      this.showAddLineItemDialog = true;
    }

    addNewLineItem() {
      this.newLineItem.id = --this.lineItemCounter;
      this.newLineItems.push(this.newLineItem);

      this.newLineItem = Object.assign({}, this.$store.getters['jobCards/emptyLineItem']);

      this.$nextTick(() => {
        if (this.$refs.lineItemForm) {
          ((this.$refs.lineItemForm) as any).resetValidation();
        }
      });

      this.$nextTick(() => {
        if (this.$refs.addLineItemForm) {
          ((this.$refs.addLineItemForm) as any).resetValidation();
        }
      });
    }

    deleteLineItem(deleteItem: JobCardLineItem) {
      this.newLineItems = this.newLineItems.filter((item: JobCardLineItem) => item.id != deleteItem.id);
    }

    getLineItemTotal() {
      const tmpLineItems = this.lineItems.concat(this.newLineItems);
      return tmpLineItems.length > 0 ? tmpLineItems.map((item: JobCardLineItem) => item.cost).reduce((prev, next) => prev + next) : 0.00;
    }

    async saveLineItems() {
      try {
        this.addLineItemsClicked = true;

        const response = await JobCardService.createLineItems({ jobCardId: this.clickedItem.id, lineItems: this.newLineItems });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listOpen(true);
            await this.listLineItems(this.clickedItem);
            this.filterJobCards();
            this.showAddLineItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - saveLineItems - 2278 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.addLineItemsClicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Close/Approve/Delete
    launchCloseItemDialog(item: JobCard) {
      this.clickedItem = item;

      this.action = 'CLOSE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #00a4e3">complete</span> the Job Card for the <br/>'
        + '<span style="font-weight: 700;">' + (item.secondaryJobType ? item.secondaryJobType : item.jobType) + '</span> on '
        + '<span style="font-weight: 700;">' + item.vehicle1Code + (this.clickedItem.vehicle2Code ? ' and ' + this.clickedItem.vehicle2Code : '') + '</span>'
        + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchApproveItemDialog(item: JobCard) {
      this.clickedItem = item;

      this.action = 'APPROVE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #00a4e3">approve</span> the Job Card for the <br/>'
        + '<span style="font-weight: 700;">' + (item.secondaryJobType ? item.secondaryJobType : item.jobType) + '</span> on '
        + '<span style="font-weight: 700;">' + item.vehicle1Code + (this.clickedItem.vehicle2Code ? ' and ' + this.clickedItem.vehicle2Code : '') + '</span>'
        + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    launchDeleteItemDialog(item: JobCard) {
      this.clickedItem = item;

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700; color: #C01212">delete</span> the Job Card for the <br/>'
        + '<span style="font-weight: 700;">' + (item.secondaryJobType ? item.secondaryJobType : item.jobType) + '</span> on '
        + '<span style="font-weight: 700;">' + item.vehicle1Code + (this.clickedItem.vehicle2Code ? ' and ' + this.clickedItem.vehicle2Code : '') + '</span>'
        + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'CLOSE') {
        await this.doClose();
      } else if (this.action === 'APPROVE') {
        await this.doApprove();
      } else if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doClose() {
      try {
        this.confirmActionDelay = true;

        const response = await JobCardService.close({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);

            // vue-admin#122 if this was a service / wheel alignment job, we need to fetch an updated list of vehicles from the DB because the updated odo numbers need to display
            if (this.clickedItem.jobType === 'Service' || this.clickedItem.jobType === 'Wheel Alignment') {
              await VehicleService.listActive(true);
            }
 
            await this.listOpen(true);
            await this.listClosed(true);
            this.filterJobCards();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doClose - 2371 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doApprove() {
      try {
        this.confirmActionDelay = true;

        const response = await JobCardService.approve({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listClosed(true);
            this.filterJobCards();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doApprove - 2406 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await JobCardService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listOpen(true);
            await this.listClosed(true);
            this.filterJobCards();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('JobCards.vue - doDelete - 2441 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
